/**
 * StoryProgressBar Component
 * 
 * Displays a vertical progress bar showing where persona interactions will occur
 * - RoleRanger (roleplay) markers
 * - QuizCritter (quiz) markers
 * 
 * Features:
 * - Positioned on left edge of story content
 * - Markers show at precise story_position percentages
 * - Tooltips display character names and question previews
 * - Responsive layout adjusts with reading progress section
 */

import React, { useState, useEffect, useRef } from 'react';
import { RoleplayOpportunity, QuizOpportunity } from '../services/storyAnalysisService';
import './StoryProgressBar.css';

interface PersonaTrigger {
  id: number;
  type: 'roleplay' | 'quiz';
  position: number; // Percentage (0-100)
  label: string;
  tooltip: string;
}

interface StoryProgressBarProps {
  roleplayOpportunities: RoleplayOpportunity[];
  quizOpportunities: QuizOpportunity[];
  currentReadingPosition?: number; // Current reading progress (0-100)
  storyContentHeight?: number; // Height of the story content area in pixels
  activePersona?: string | null; // Currently active persona (for hint display and animations)
}

export const StoryProgressBar: React.FC<StoryProgressBarProps> = ({
  roleplayOpportunities,
  quizOpportunities,
  currentReadingPosition = 0,
  storyContentHeight = 600,
  activePersona = null
}) => {
  const [triggers, setTriggers] = useState<PersonaTrigger[]>([]);
  const [hoveredTriggerId, setHoveredTriggerId] = useState<number | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  // Process opportunities into trigger markers
  useEffect(() => {
    const allTriggers: PersonaTrigger[] = [];

    // Add roleplay triggers
    roleplayOpportunities.forEach((opp, index) => {
      allTriggers.push({
        id: allTriggers.length + 1,
        type: 'roleplay',
        position: opp.story_position,
        label: `🎭 ${opp.character_name}`,
        tooltip: `RoleRanger: Play as ${opp.character_name} - ${opp.situation}`
      });
    });

    // Add quiz triggers
    quizOpportunities.forEach((opp, index) => {
      allTriggers.push({
        id: allTriggers.length + 1,
        type: 'quiz',
        position: opp.story_position,
        label: '📚 Quiz',
        tooltip: `QuizCritter: ${opp.question_text}`
      });
    });

    // Sort by position
    allTriggers.sort((a, b) => a.position - b.position);

    setTriggers(allTriggers);
  }, [roleplayOpportunities, quizOpportunities]);

  // Calculate marker positions based on story content height
  const getMarkerStyle = (position: number) => {
    const topPercent = position;
    return {
      top: `${topPercent}%`
    };
  };

  // Determine if icon should bounce based on active persona
  const shouldBounce = (triggerType: string) => {
    if (activePersona === 'role_ranger' && triggerType === 'roleplay') return true;
    if (activePersona === 'quiz_critter' && triggerType === 'quiz') return true;
    return false;
  };
  
  // Find the trigger closest to current reading position that matches the active persona
  const getClosestActiveTrigger = () => {
    if (!activePersona || currentReadingPosition <= 0) return null;
    
    // Filter triggers that match the active persona
    const matchingTriggers = triggers.filter(t => {
      if (activePersona === 'role_ranger' && t.type === 'roleplay') return true;
      if (activePersona === 'quiz_critter' && t.type === 'quiz') return true;
      return false;
    });
    
    if (matchingTriggers.length === 0) return null;
    
    // Find the trigger closest to current reading position
    // Prefer triggers at or slightly before current position (within ~10% range)
    let closestTrigger = matchingTriggers[0];
    let smallestDistance = Math.abs(currentReadingPosition - closestTrigger.position);
    
    for (const trigger of matchingTriggers) {
      const distance = Math.abs(currentReadingPosition - trigger.position);
      // Prioritize triggers that are at or just before current position
      if (distance < smallestDistance && trigger.position <= currentReadingPosition + 5) {
        closestTrigger = trigger;
        smallestDistance = distance;
      }
    }
    
    // Only show hint if trigger is within reasonable range (~15% of reading position)
    if (smallestDistance <= 15) {
      return closestTrigger;
    }
    
    return null;
  };
  
  const closestActiveTrigger = getClosestActiveTrigger();
  
  // Determine if hint should be shown for this specific trigger (only the closest active one)
  const shouldShowHint = (triggerId: number) => {
    return closestActiveTrigger?.id === triggerId;
  };

  return (
    <div 
      className="story-progress-bar-container"
      ref={barRef}
      style={{ height: storyContentHeight }}
    >
      {/* Vertical bar */}
      <div className="story-progress-bar">
        {/* Current reading position indicator */}
        {currentReadingPosition > 0 && (
          <div 
            className="reading-position-indicator"
            style={{ top: `${currentReadingPosition}%` }}
          >
            <div className="reading-position-line" />
            <div className="reading-position-dot" />
          </div>
        )}

        {/* Persona trigger markers */}
        {triggers.map((trigger) => (
          <div
            key={trigger.id}
            className={`trigger-marker trigger-marker-${trigger.type} ${shouldBounce(trigger.type) ? 'trigger-active' : ''}`}
            style={getMarkerStyle(trigger.position)}
            onMouseEnter={() => setHoveredTriggerId(trigger.id)}
            onMouseLeave={() => setHoveredTriggerId(null)}
          >
            <div className="trigger-icon">
              {trigger.type === 'roleplay' ? '🎭' : '📚'}
            </div>
            
            {/* Active Persona Hint - Show only for first active trigger */}
            {shouldShowHint(trigger.id) && (
              <div className="persona-active-hint-callout">
                <div className="hint-callout-content">
                  <div className="hint-callout-title">
                    {trigger.type === 'roleplay' ? '🎭 RoleRanger Active!' : '🎮 QuizCritter Active!'}
                  </div>
                  <div className="hint-callout-text">
                    Hey!! {trigger.type === 'roleplay' ? 'RoleRanger' : 'QuizCritter'} wants to talk to you. keep responding until you hear <strong>"Happy Reading"</strong> before resuming reading the story.
                  </div>
                </div>
                <div className="hint-callout-arrow" />
              </div>
            )}
            
            {/* Tooltip - Show on hover when not active */}
            {hoveredTriggerId === trigger.id && !shouldShowHint(trigger.id) && (
              <div className="trigger-tooltip">
                <div className="tooltip-content">
                  <div className="tooltip-header">{trigger.label}</div>
                  <div className="tooltip-body">{trigger.tooltip}</div>
                  <div className="tooltip-position">At {trigger.position.toFixed(1)}% of story</div>
                </div>
                <div className="tooltip-arrow" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bar labels */}
      <div className="progress-bar-labels">
        <div className="bar-label bar-label-top">Start</div>
        <div className="bar-label bar-label-bottom">End</div>
      </div>
    </div>
  );
};

export default StoryProgressBar;
